import React from 'react';
import { FileText, Shield, DollarSign, Scale, Hammer, Clock, AlertTriangle, HelpCircle } from 'lucide-react';
import { Breadcrumb } from '../components/Breadcrumb';
import SEO from '../components/SEO';

const terms = [
  {
    icon: DollarSign,
    title: "Payment Terms",
    items: [
      "Payment for construction work should be as per Modes of Payment only. Any partial amounts will not be received by first party.",
      "Payment should be made through cash only. Payment through Cheques/Account transfer will not be accepted.",
      "Basic rate of cement is ₹380/bag (ACC or Ultratech) and steel is ₹60/KG (TMT). Any escalation in the price has to be borne by the second party."
    ]
  },
  {
    icon: Scale,
    title: "Additional Work & Disputes",
    items: [
      "Any additional work coming up during construction period, that additional cost of construction will be settled by two parties at a decent rate with mutual consultation.",
      "If there are any queries or difference between the two parties while construction, such difference shall be resolved by mutual consultation.",
      "Once construction starts the project should not get delayed/stop at any intermediate points due to lack of payment, because construction material prices may hike day to day as the project delays. If we come across such situations, such difference shall be resolved by mutual consultation at decent rates."
    ]
  },
  {
    icon: Hammer,
    title: "Scope of Work",
    items: [
      "Only provision will be made for wardrobes and showcase wherever necessary.",
      "Wardrobe & Italian Kitchen fixtures will have to be borne by the second party (Interior Works).",
      "Cost of water used during construction will be met by the second party.",
      "Curing will be at the cost of the first party.",
      "Watchman during the period of construction will be appointed by the second party.",
      "Rain water outlet 3\" PVC pipe from roof up to ground level will be provided.",
      "Ramp will be provided over the drain in front of the gate."
    ]
  },
  {
    icon: Shield,
    title: "Responsibilities",
    items: [
      "First party is not responsible for any illegal issues with respect to plot/neighbor issues.",
      "All departmental charges such as BDA, BWSSB, DB, BESCOM, Panchayath, etc. will have to be borne by the second party."
    ]
  }
];

const Terms = () => {
  return (
    <div>
      <SEO
        title="Terms & Conditions"
        description="Terms and conditions of Aashi Constructions. Read our policies on payments, warranties, and construction agreements."
        keywords="terms and conditions construction, Aashi Constructions policies"
        url="/terms"
      />
      <Breadcrumb items={[{ label: 'Terms & Conditions' }]} />

      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 md:h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.pexels.com/photos/1571459/pexels-photo-1571459.jpeg"
          alt="Terms and Conditions"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-3 sm:mb-4">
            Terms & Conditions
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            Please read our terms and conditions carefully before engaging our construction services
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 md:space-y-12">
            {terms.map((section, index) => {
              const IconComponent = section.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden"
                >
                  <div className="p-6 sm:p-8 md:p-10">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-brand-600" />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                        {section.title}
                      </h2>
                    </div>
                    <ul className="space-y-4">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <div className="w-2 h-2 bg-brand-600 rounded-full mt-2 mr-3 flex-shrink-0" />
                          <span className="text-gray-700 text-sm sm:text-base leading-relaxed">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Note Section */}
          <div className="mt-10 p-6 sm:p-8 bg-amber-50 border border-amber-200 rounded-2xl">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-amber-800 mb-2">Important Note</h3>
                <p className="text-amber-700 text-sm sm:text-base leading-relaxed">
                  These terms and conditions are part of the construction agreement between Aashi Constructions and the client (second party). All disputes shall be resolved by mutual consultation. Please ensure you understand all terms before proceeding with the project.
                </p>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-10 text-center">
            <p className="text-gray-600 text-sm sm:text-base">
              Have questions about our terms? Feel free to{' '}
              <a href="/contact" className="text-brand-600 hover:text-brand-700 font-medium underline">
                contact us
              </a>{' '}
              for clarification.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
